import { SegmentedControl } from "@/components/ui/segmented-control";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  BookOpen,
  Monitor,
  Smartphone,
  Search,
  ArrowUp,
  Expand,
  X,
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { MobileBackButton } from "../misc/MobileBackButton";
import { useGetIdentity } from "ra-core";
import { effectiveRole } from "../sales/roles";
import { visibleHelpChapters } from "./visibleHelpChapters";
import screenshots from "./screenshots.json";
import "./help.css";

type Screenshot = {
  src: string;
  width: number;
  height: number;
  marks: { label: string; x: number; y: number; w: number; h: number }[];
};
const pictures = screenshots as Record<string, Screenshot>;
function Illustration({
  picture,
  title,
}: {
  picture: Screenshot;
  title: string;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const visual = (
    <div className="help-picture">
      <img
        src={`${import.meta.env.BASE_URL}${picture.src}`}
        alt={`${title}: учебный пример интерфейса`}
        width={picture.width}
        height={picture.height}
        loading="lazy"
      />
      <svg
        aria-hidden="true"
        viewBox={`0 0 ${picture.width} ${picture.height}`}
      >
        <defs>
          <marker
            id={`arrow-${picture.src.replace(/\W/g, "")}`}
            markerWidth="7"
            markerHeight="7"
            refX="6"
            refY="3.5"
            orient="auto"
          >
            <path d="M0 0 L7 3.5 L0 7" fill="#c46b38" />
          </marker>
        </defs>
        {picture.marks.map((m, i) => {
          const x = Math.max(18, Math.min(picture.width - 18, m.x - 22));
          const y = Math.max(18, m.y - 24);
          return (
            <g key={i}>
              <rect
                x={m.x - 3}
                y={m.y - 3}
                width={m.w + 6}
                height={m.h + 6}
                rx="8"
                fill="#c46b3810"
                stroke="#c46b38"
                strokeWidth="2"
              />
              <path
                d={`M${x} ${y} L${m.x + Math.min(20, m.w / 2)} ${m.y + Math.min(12, m.h / 2)}`}
                stroke="#c46b38"
                strokeWidth="2"
                markerEnd={`url(#arrow-${picture.src.replace(/\W/g, "")})`}
              />
              <circle
                cx={x}
                cy={y}
                r="14"
                fill="#c46b38"
                stroke="white"
                strokeWidth="2"
              />
              <text
                x={x}
                y={y + 5}
                textAnchor="middle"
                fill="white"
                fontSize="15"
                fontWeight="700"
              >
                {i + 1}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
  return (
    <figure
      className={`help-figure ${picture.width < 600 ? "help-phone" : ""}`}
    >
      <button
        className="help-image-button"
        onClick={() => dialog.current?.showModal()}
        aria-label={`Увеличить: ${title}`}
      >
        {visual}
        <span>
          <Expand size={15} /> Увеличить
        </span>
      </button>
      <figcaption>
        <span className="help-example">Учебные данные</span>
        {picture.marks.map((m, i) => (
          <span key={i}>
            <b>{i + 1}</b>
            {m.label}
          </span>
        ))}
      </figcaption>
      <dialog
        ref={dialog}
        className="help-zoom"
        aria-label={title}
        onClick={(e) => {
          if (e.target === e.currentTarget) dialog.current?.close();
        }}
      >
        <button
          data-mobile-control="icon"
          className="help-close"
          onClick={() => dialog.current?.close()}
          aria-label="Закрыть изображение"
        >
          <X />
        </button>
        {visual}
      </dialog>
    </figure>
  );
}
export const HelpPage = () => {
  const { identity } = useGetIdentity();
  const chapters = identity ? visibleHelpChapters(effectiveRole(identity)) : [];
  const mobile = useIsMobile();
  const [device, setDevice] = useState<"desktop" | "mobile">(
    window.matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop",
  );
  const [query, setQuery] = useState("");
  const [params] = useSearchParams();
  const section = params.get("section");
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const target = document.getElementById(
        section ? `help-${section}` : "help-top",
      );
      target?.scrollIntoView({ block: "start" });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [section]);
  const normalize = (text: string) =>
    text.toLocaleLowerCase("ru").replaceAll("ё", "е");
  const words = normalize(query)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => (word.length > 5 ? word.slice(0, -2) : word));
  const filtered = chapters.filter((chapter) => {
    const text = normalize(
      [
        chapter.title,
        chapter.summary,
        ...chapter.desktop,
        ...chapter.mobile,
        ...chapter.controls.flatMap((control) => [
          control.name,
          control.action,
        ]),
        chapter.tip,
      ].join(" "),
    );
    return words.every((word) => text.includes(word));
  });
  const nav = (
    <nav
      aria-label="Разделы инструкции"
      style={{
        gridTemplateRows: mobile
          ? undefined
          : `repeat(${Math.max(1, Math.ceil(filtered.length / 2))}, auto)`,
      }}
    >
      {filtered.map((chapter) => (
        <Link
          key={chapter.id}
          to={`/help?section=${chapter.id}`}
          onClick={() => setQuery("")}
          aria-current={section === chapter.id ? "location" : undefined}
        >
          <span>{String(chapters.indexOf(chapter) + 1).padStart(2, "0")}</span>
          {chapter.title}
        </Link>
      ))}
    </nav>
  );
  const content = (
    <div className="help-layout" id="help-top">
      <aside className="help-sidebar">
        <p>Содержание</p>
        {nav}
      </aside>
      <main className="help-main">
        <header className="help-hero">
          <BookOpen size={32} />
          <p className="help-eyebrow">ROVERO CRM · Руководство сотрудника</p>
          <h1>Как пользоваться</h1>
          <p>
            От первого входа до выполненной задачи. Выберите нужную функцию в
            содержании или найдите её по названию.
          </p>
          <p className="help-muted">
            Инструкции для компьютера и телефона. Все скриншоты сделаны в
            учебной базе с вымышленными данными.
          </p>
        </header>
        <div className="help-tools">
          <label className="help-search">
            <Search size={19} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Например, поручить задачу"
              aria-label="Поиск по инструкции"
            />
          </label>
          <SegmentedControl
            value={device}
            onValueChange={setDevice}
            label="Версия инструкции"
            className="help-device-switch"
            options={[
              {
                value: "desktop",
                label: (
                  <>
                    <Monitor size={17} />
                    Компьютер
                  </>
                ),
              },
              {
                value: "mobile",
                label: (
                  <>
                    <Smartphone size={17} />
                    Телефон
                  </>
                ),
              },
            ]}
          />
        </div>
        {query && (
          <p role="status">
            Найдено разделов: {filtered.length}.{" "}
            <button className="help-text-button" onClick={() => setQuery("")}>
              Сбросить поиск
            </button>
          </p>
        )}
        {filtered.length === 0 && (
          <p className="help-empty">
            Попробуйте другое слово: «контакт», «статус», «комментарий» или
            «настройки».
          </p>
        )}
        <section
          className="help-contents"
          aria-labelledby="help-contents-title"
        >
          <h2 id="help-contents-title">Содержание инструкции</h2>
          {nav}
        </section>
        {filtered.map((chapter) => {
          const picture = chapter.image
            ? pictures[`${device}-${chapter.image}`]
            : undefined;
          return (
            <section
              className="help-chapter"
              key={chapter.id}
              id={`help-${chapter.id}`}
            >
              <div className="help-chapter-heading">
                <span className="help-number">
                  {String(chapters.indexOf(chapter) + 1).padStart(2, "0")}
                </span>
                <h2>
                  <Link to={`/help?section=${chapter.id}`}>
                    {chapter.title}
                  </Link>
                </h2>
              </div>
              <p>{chapter.summary}</p>
              <p className="help-device-label">
                {device === "desktop" ? (
                  <Monitor size={16} />
                ) : (
                  <Smartphone size={16} />
                )}
                {device === "desktop" ? "На компьютере" : "На телефоне"}
              </p>
              <ol className="help-steps">
                {chapter[device].map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
              {picture && (
                <Illustration picture={picture} title={chapter.title} />
              )}
              <h3>Кнопки и элементы</h3>
              <dl className="help-controls">
                {chapter.controls.map((control) => (
                  <div key={control.name}>
                    <dt>{control.name}</dt>
                    <dd>{control.action}</dd>
                  </div>
                ))}
              </dl>
              {chapter.tip && <p className="help-tip">{chapter.tip}</p>}
              <div className="help-chapter-footer">
                <Link
                  to="/help"
                  onClick={() => {
                    setQuery("");
                    document
                      .getElementById("help-top")
                      ?.scrollIntoView({ block: "start" });
                  }}
                >
                  <ArrowUp size={15} />К содержанию
                </Link>
                <button
                  onClick={() =>
                    setDevice(device === "desktop" ? "mobile" : "desktop")
                  }
                >
                  {device === "desktop"
                    ? "Показать для телефона"
                    : "Показать для компьютера"}
                </button>
              </div>
            </section>
          );
        })}
      </main>
    </div>
  );
  return mobile ? (
    <>
      <MobileHeader>
        <MobileBackButton to="/" />
        <h1 className="text-xl font-semibold">Как пользоваться</h1>
      </MobileHeader>
      <MobileContent>{content}</MobileContent>
    </>
  ) : (
    content
  );
};
